import React, { useState } from 'react';

import { Link } from 'react-router-dom';

import {
  BankOutlined,
  CheckOutlined,
  CloseOutlined,
  DownOutlined,
  IdcardOutlined,
  MessageOutlined,
  NotificationOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import {
  CustomerType,
  TenantFeatureFlag,
  TenantProductName,
  TenantUserPermission,
} from '@recurrency/core-api-schema/dist/common/enums';
import { Grid, Menu } from 'antd';

import { hasChatWithDataAccess } from 'pages/chat/access';

import { AsyncMultiSelect } from 'components/AsyncSelect/AsyncMultiSelect';
import { useTenantSlugSelectProps } from 'components/AsyncSelect/useAsyncMultiSelectProps';
import { Button } from 'components/Button';
import { useChatDrawer } from 'components/ChatAssistant/ChatContext';
import { Dropdown } from 'components/Dropdown';
import { InventiveButton } from 'components/Inventive/InventiveButton';
import { TaskModal } from 'components/recipes/TaskModal';
import { Tooltip } from 'components/Tooltip';

import { useGlobalApp } from 'hooks/useGlobalApp';

import { showAsyncModal } from 'utils/asyncModal';
import { setLocalStorageItem, LocalStorageKey } from 'utils/localStorage';
import {
  hasUserPermission,
  shouldShowFeatureFlag,
  roleToString,
  reloadPageWithTenantSlug,
  shouldShowProduct,
} from 'utils/roleAndTenant';
import { routes } from 'utils/routes';
import { CtaName, CtaType, track, TrackEvent } from 'utils/track';

import { GlobalCompanyFilterSelect } from './GlobalCompanyFilterSelect';
import { GlobalSearch } from './GlobalSearch';
import { HomePageButton } from './HomePageButton';
import * as Styled from './PrimaryNavigation.style';
import { UserProfileMenu } from './UserProfileMenu';

export interface PrimaryNavigationProps {
  isDesktopView: boolean;
  onLogoClick: () => void;
  isPopupMode?: boolean;
}

export const PrimaryNavigation = ({ isDesktopView, onLogoClick, isPopupMode }: PrimaryNavigationProps) => {
  const { tenants, activeTenant, activeErpRole, activeUser, roles } = useGlobalApp();
  const chatDrawer = useChatDrawer();

  const isTabletOrMobileView = !(Grid.useBreakpoint()?.lg ?? false);

  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const shouldShowNonSearch = !isTabletOrMobileView || !isMobileSearchOpen;
  const tenantSlugSelectProps = useTenantSlugSelectProps(tenants);

  const isInSetupMode = !activeTenant.isActive;

  // inventive disabled while it's future is re-evaluated
  // see https://recurrency.slack.com/archives/C01D3ELUZFZ/p1761321901231269?thread_ts=1761321586.977309&cid=C01D3ELUZFZ
  const shouldShowInventive = false;

  // each item mirrors the gates on its route, so the menu never offers a page the user would be bounced from
  const canCreateQuote =
    shouldShowFeatureFlag(activeTenant, activeUser, TenantFeatureFlag.OrdersCreateQuote) &&
    hasUserPermission(activeTenant, TenantUserPermission.AccessOrdersQuotesView);
  const canCreateOrder =
    shouldShowFeatureFlag(activeTenant, activeUser, TenantFeatureFlag.OrdersCreateOrder) &&
    hasUserPermission(activeTenant, TenantUserPermission.AccessOrdersOrdersView);
  const canCreateFrontCounterOrder =
    shouldShowFeatureFlag(activeTenant, activeUser, TenantFeatureFlag.OrdersFrontCounterEnabled) &&
    hasUserPermission(activeTenant, TenantUserPermission.AccessOrdersOrdersView);
  const canCreateTask =
    shouldShowProduct(activeTenant, TenantProductName.SalesCrm) &&
    hasUserPermission(activeTenant, TenantUserPermission.ActionManageTasks);
  const canCreateProspect =
    shouldShowFeatureFlag(activeTenant, activeUser, TenantFeatureFlag.SalesCreateProspect) &&
    hasUserPermission(activeTenant, TenantUserPermission.AccessSalesCustomersView);
  const canCreateCustomer =
    shouldShowFeatureFlag(activeTenant, activeUser, TenantFeatureFlag.SalesCreateCustomer) &&
    hasUserPermission(activeTenant, TenantUserPermission.AccessSalesCustomersView);
  const canCreateRecord =
    canCreateQuote ||
    canCreateOrder ||
    canCreateFrontCounterOrder ||
    canCreateTask ||
    canCreateProspect ||
    canCreateCustomer;

  return (
    <>
      <Styled.Container data-test-id="primaryNavContainer">
        <Styled.Content isDesktopView={isDesktopView}>
          <Styled.ContentChild isDesktopView={isDesktopView} flex={0}>
            {shouldShowNonSearch && (
              <Styled.LogoWrapper onClick={onLogoClick}>
                {isTabletOrMobileView ? <Styled.MobileLogo /> : <Styled.Logo />}
              </Styled.LogoWrapper>
            )}
            {tenants && activeTenant && tenants.length > 1 && shouldShowNonSearch && (
              <Styled.DropdownWrapper>
                <AsyncMultiSelect
                  mode="single"
                  queryPlaceholder="Search tenants"
                  selectedValues={[activeTenant.slug]}
                  onSelectedValuesChange={(values) => reloadPageWithTenantSlug(values[0])}
                  selectProps={tenantSlugSelectProps}
                  triggerButton={
                    <Button size="small">
                      <BankOutlined />
                      {isDesktopView && ` ${activeTenant.name} `}
                      {!isTabletOrMobileView && <DownOutlined />}
                    </Button>
                  }
                />
              </Styled.DropdownWrapper>
            )}
            {roles && activeErpRole && roles.length > 1 && shouldShowNonSearch && (
              <Styled.DropdownWrapper>
                <Dropdown
                  overlay={
                    <Menu>
                      {roles.map((role, idx) => (
                        <Menu.Item
                          key={idx}
                          onClick={() => {
                            setLocalStorageItem(LocalStorageKey.ActiveErpRole, role);
                            window.location.hash = '';
                            window.location.reload();
                          }}
                        >
                          {!isDesktopView && role.foreignId === activeErpRole.foreignId && <CheckOutlined />}
                          {roleToString(role)}
                        </Menu.Item>
                      ))}
                    </Menu>
                  }
                >
                  <Button size="small">
                    <IdcardOutlined />
                    {isDesktopView && ` ${roleToString(activeErpRole)} `}
                    {!isTabletOrMobileView && <DownOutlined />}
                  </Button>
                </Dropdown>
              </Styled.DropdownWrapper>
            )}
            {shouldShowNonSearch && <GlobalCompanyFilterSelect isDesktopView={isDesktopView} />}
          </Styled.ContentChild>
          {!isInSetupMode && !isPopupMode && shouldShowProduct(activeTenant, TenantProductName.GlobalSearch) && (
            <Styled.ContentChild isDesktopView={isDesktopView} isMobileSearchOpen={isMobileSearchOpen} flex={1}>
              <Styled.SearchInput isDesktopView={isDesktopView}>
                {(!isTabletOrMobileView || isMobileSearchOpen) && <GlobalSearch isDesktopView={isDesktopView} />}
                {isTabletOrMobileView &&
                  (isMobileSearchOpen ? (
                    <Styled.LeftPadButton>
                      <Button size="small" onClick={() => setIsMobileSearchOpen(false)}>
                        <CloseOutlined />
                      </Button>
                    </Styled.LeftPadButton>
                  ) : (
                    <Button size="small" onClick={() => setIsMobileSearchOpen(true)}>
                      <SearchOutlined />
                    </Button>
                  ))}
              </Styled.SearchInput>
              {shouldShowNonSearch && !isPopupMode && canCreateRecord && (
                <Styled.CreateButton>
                  <Dropdown
                    overlay={
                      <Menu>
                        {canCreateQuote && (
                          <Menu.Item>
                            <Link
                              to={routes.orders.quoteNew()}
                              onClick={() =>
                                track(TrackEvent.Nav_CtaClick, {
                                  ctaName: CtaName.NewQuote,
                                  ctaType: CtaType.PrimaryNavNewRecord,
                                })
                              }
                            >
                              Quote
                            </Link>
                          </Menu.Item>
                        )}
                        {canCreateOrder && (
                          <Menu.Item>
                            <Link
                              to={routes.orders.orderNew()}
                              onClick={() =>
                                track(TrackEvent.Nav_CtaClick, {
                                  ctaName: CtaName.NewOrder,
                                  ctaType: CtaType.PrimaryNavNewRecord,
                                })
                              }
                            >
                              Order
                            </Link>
                          </Menu.Item>
                        )}
                        {canCreateFrontCounterOrder && (
                          <Menu.Item>
                            <Link
                              to={routes.orders.orderNew({ frontCounter: true })}
                              onClick={() =>
                                track(TrackEvent.Nav_CtaClick, {
                                  ctaName: CtaName.NewOrder,
                                  ctaType: CtaType.PrimaryNavNewRecord,
                                })
                              }
                            >
                              Front Counter
                            </Link>
                          </Menu.Item>
                        )}
                        {canCreateTask && (
                          <Menu.Item
                            onClick={() => {
                              track(TrackEvent.Nav_CtaClick, {
                                ctaName: CtaName.NewTask,
                                ctaType: CtaType.PrimaryNavNewRecord,
                              });
                              showAsyncModal(TaskModal, {});
                            }}
                          >
                            Task
                          </Menu.Item>
                        )}
                        {canCreateProspect && (
                          <Menu.Item>
                            <Link
                              to={routes.sales.customerNew()}
                              onClick={() =>
                                track(TrackEvent.Nav_CtaClick, {
                                  ctaName: CtaName.NewProspect,
                                  ctaType: CtaType.PrimaryNavNewRecord,
                                })
                              }
                            >
                              Prospect
                            </Link>
                          </Menu.Item>
                        )}
                        {canCreateCustomer && (
                          <Menu.Item>
                            <Link
                              to={routes.sales.customerNew({ type: CustomerType.Customer })}
                              onClick={() =>
                                track(TrackEvent.Nav_CtaClick, {
                                  ctaName: CtaName.NewCustomer,
                                  ctaType: CtaType.PrimaryNavNewRecord,
                                })
                              }
                            >
                              Customer
                            </Link>
                          </Menu.Item>
                        )}
                      </Menu>
                    }
                  >
                    <Button size="small">
                      <PlusOutlined />
                      {isDesktopView && ' New Record'}
                      {!isTabletOrMobileView && <DownOutlined />}
                    </Button>
                  </Dropdown>
                </Styled.CreateButton>
              )}
            </Styled.ContentChild>
          )}
          {shouldShowNonSearch && (
            <Styled.ContentChild isDesktopView={isDesktopView} flex={0}>
              {!isPopupMode && (
                <Tooltip title="Recurrency Changelog" placement="bottom">
                  <Styled.IconOnlyButton className="headwayapp-badge">
                    <NotificationOutlined />
                  </Styled.IconOnlyButton>
                </Tooltip>
              )}
              <Tooltip title="Help Center" placement="bottom">
                <Styled.IconOnlyButton onClick={() => window.open('https://help.recurrency.com', '_blank')}>
                  <QuestionCircleOutlined />
                </Styled.IconOnlyButton>
              </Tooltip>
              {hasChatWithDataAccess(activeTenant, activeUser) && (
                <Tooltip title="Cue" placement="bottom">
                  <Styled.IconOnlyButton onClick={() => chatDrawer?.openChat()}>
                    <MessageOutlined />
                  </Styled.IconOnlyButton>
                </Tooltip>
              )}
              <HomePageButton />
              {shouldShowInventive && (
                <Tooltip title="Recurrency Copilot" placement="bottom">
                  <Styled.IconOnlyButton>
                    <InventiveButton />
                  </Styled.IconOnlyButton>
                </Tooltip>
              )}
              <Styled.MenuItem>
                <Dropdown
                  arrow
                  placement="bottomRight"
                  overlay={
                    <Menu>
                      <UserProfileMenu />
                    </Menu>
                  }
                >
                  <Styled.MenuItemChild
                    role="button"
                    tabIndex={0}
                    aria-label={`Logged in as ${activeUser?.fullName || 'Unknown user'}`}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault(); // stop Space from scrolling the page
                        e.currentTarget.click();
                      }
                    }}
                  >
                    <Tooltip title={`Logged in as ${activeUser?.fullName || 'Unknown user'}`} placement="bottom">
                      <Styled.Icon>
                        <Styled.Avatar icon={<Styled.UserProfileIcon />} />
                      </Styled.Icon>
                    </Tooltip>
                    {!isTabletOrMobileView && (
                      <Styled.SmallIcon>
                        <DownOutlined />
                      </Styled.SmallIcon>
                    )}
                  </Styled.MenuItemChild>
                </Dropdown>
              </Styled.MenuItem>
            </Styled.ContentChild>
          )}
        </Styled.Content>
      </Styled.Container>
    </>
  );
};
